// ─── RECOVERY ROADMAP ─────────────────────────────────────────────────────────
// Roadmap.jsx — Enhanced Mobile/PWA UI. Full logic preserved.
// Route: /roadmap

import React, { useState, useEffect, useRef, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { auth } from "../firebase";
import { signOut } from "firebase/auth";
import { MdOutlineLightMode, MdModeNight, MdFilterVintage } from "react-icons/md";
import { FaUserCircle } from "react-icons/fa";
import {
  CATEGORIES, TRIGGERS, WITHDRAWAL_INFO, BADGES as ALL_BADGES,
} from "../components/roadmapContent";
import {
  hasActiveRoadmap, getConfig, createRoadmap, deleteRoadmap,
  getPlanDays, getTodayDay, getTodayData, getDayData,
  getProgress, getDayProgress, completeDay, markStruggled, handleRelapse,
  getStats, getStreak, logUrge, getUrgesByDay, getTodayUrgeCount,
  getBadges, getCategoryForAddiction, getAddictionMeta,
  getHoursFree, getDaysFree, upgrade60Day,
} from "../components/RoadmapEngine";

// ─── INJECTED CSS ─────────────────────────────────────────────────────────────
const rmCss = `
  @import url('https://fonts.cdnfonts.com/css/satoshi');
  @import url('https://fonts.googleapis.com/css2?family=Fraunces:ital,opsz,wght@0,9..144,300;0,9..144,500;0,9..144,700;1,9..144,400&family=Plus+Jakarta+Sans:wght@300;400;500;600;700;800&display=swap');

  /* ── Root Variables (light) ── */
  .rm-root {
    --rm-bg: #F9F4F0;
    --rm-card: #FFFFFF;
    --rm-surface: #F4F7F9;
    --rm-brand: #5A6993;
    --rm-brand-deep: #323B52;
    --rm-brand-light: #A1B0D8;
    --rm-brand-wash: rgba(90,105,147,.07);
    --rm-sage: #7B8F8A;
    --rm-sage-wash: rgba(123,143,138,.09);
    --rm-peach: #E6A184;
    --rm-peach-wash: rgba(230,161,132,.09);
    --rm-danger: #dc2626;
    --rm-danger-wash: rgba(220,38,38,.07);
    --rm-ink: #2D364D;
    --rm-ink-soft: #4A5568;
    --rm-muted: #9C8474;
    --rm-sand: #D6D2C4;
    --rm-sand-light: #EBE7DF;
    --rm-cloud: #F4F7F9;
    --rm-font: 'Satoshi','Plus Jakarta Sans',sans-serif;
    --rm-font-display: 'Fraunces',serif;
    --rm-r: 14px;
    --rm-r-lg: 20px;
    --rm-r-xl: 28px;
    --rm-sh-sm: 0 1px 3px rgba(45,54,77,.04),0 2px 8px rgba(45,54,77,.04);
    --rm-sh-md: 0 2px 8px rgba(45,54,77,.05),0 8px 24px rgba(45,54,77,.06);
    --rm-sh-lg: 0 4px 16px rgba(45,54,77,.07),0 16px 48px rgba(45,54,77,.08);
    --rm-ease: .2s cubic-bezier(.4,0,.2,1);
    --rm-spring: .4s cubic-bezier(.22,.68,0,1.1);
    /* phase colors */
    --rm-phase-a: #5A6993;
    --rm-phase-r: #7B8F8A;
    --rm-phase-f: #E6A184;
    --rm-phase-d: #323B52;
    --rm-phase-m: #8a7b9b;
    --rm-phase-l: #c4845a;
  }

  /* ── Dark theme overrides ── */
  body.dark-theme .rm-root {
    --rm-bg: #151820;
    --rm-card: #1e2230;
    --rm-surface: #1a1e2b;
    --rm-brand: #8ea4c8;
    --rm-brand-deep: #a1b0d8;
    --rm-brand-light: #6b7ea8;
    --rm-brand-wash: rgba(142,164,200,.1);
    --rm-sage: #8faaa3;
    --rm-sage-wash: rgba(143,170,163,.1);
    --rm-peach: #e8b09a;
    --rm-peach-wash: rgba(232,176,154,.08);
    --rm-danger: #f87171;
    --rm-danger-wash: rgba(248,113,113,.08);
    --rm-ink: #e0ddd8;
    --rm-ink-soft: #b8b2aa;
    --rm-muted: #9a9088;
    --rm-sand: #4a4640;
    --rm-sand-light: #2e2c28;
    --rm-cloud: #1c2030;
    --rm-sh-sm: 0 1px 3px rgba(0,0,0,.2),0 2px 8px rgba(0,0,0,.15);
    --rm-sh-md: 0 2px 8px rgba(0,0,0,.18),0 8px 24px rgba(0,0,0,.2);
    --rm-sh-lg: 0 4px 12px rgba(0,0,0,.2),0 16px 48px rgba(0,0,0,.28);
  }

  /* ── Zen theme ── */
  body.zen-theme .rm-root {
    --rm-brand: #7B8F8A;
    --rm-brand-wash: rgba(123,143,138,.07);
    --rm-phase-a: #7B8F8A;
  }

  /* ── Base ── */
  .rm-root *, .rm-root *::before, .rm-root *::after {
    box-sizing: border-box;
    -webkit-tap-highlight-color: transparent;
  }
  .rm-root {
    font-family: var(--rm-font);
    background: var(--rm-bg);
    color: var(--rm-ink);
    min-height: 100vh;
    min-height: 100dvh;
    -webkit-font-smoothing: antialiased;
  }

  /* ── Card ── */
  .rm-card {
    background: var(--rm-card);
    border: 1px solid var(--rm-sand-light);
    border-radius: var(--rm-r-lg);
    box-shadow: var(--rm-sh-sm);
  }
  .rm-card-elevated {
    background: var(--rm-card);
    border: 1px solid var(--rm-sand-light);
    border-radius: var(--rm-r-xl);
    box-shadow: var(--rm-sh-md);
  }

  /* ── Buttons ── */
  .rm-btn-primary {
    width: 100%;
    padding: 14px;
    background: var(--rm-brand);
    color: #fff;
    border: none;
    border-radius: var(--rm-r-lg);
    font-family: var(--rm-font);
    font-weight: 700;
    font-size: .88rem;
    cursor: pointer;
    letter-spacing: .01em;
    box-shadow: 0 2px 6px rgba(90,105,147,.2), 0 4px 16px rgba(90,105,147,.12);
    transition: all var(--rm-ease);
    display: flex; align-items: center; justify-content: center; gap: 8px;
    min-height: 48px;
    position: relative; overflow: hidden;
  }
  .rm-btn-primary::after {
    content: '';
    position: absolute; inset: 0;
    background: linear-gradient(180deg, rgba(255,255,255,.1) 0%, transparent 60%);
    pointer-events: none;
  }
  .rm-btn-primary:hover:not(:disabled) { filter: brightness(1.08); transform: translateY(-1px); }
  .rm-btn-primary:active:not(:disabled) { transform: scale(.975); }
  .rm-btn-primary:disabled { opacity: .45; cursor: not-allowed; }

  .rm-btn-ghost {
    width: 100%;
    padding: 13px;
    background: none;
    color: var(--rm-muted);
    border: 1.5px solid var(--rm-sand-light);
    border-radius: var(--rm-r-lg);
    font-family: var(--rm-font);
    font-weight: 600;
    font-size: .86rem;
    cursor: pointer;
    transition: all var(--rm-ease);
    display: flex; align-items: center; justify-content: center; gap: 8px;
    min-height: 48px;
  }
  .rm-btn-ghost:hover:not(:disabled) { border-color: var(--rm-sand); color: var(--rm-ink); background: var(--rm-cloud); }
  .rm-btn-ghost:active { transform: scale(.975); }

  .rm-btn-danger {
    width: 100%;
    padding: 13px;
    background: none;
    color: var(--rm-danger);
    border: 1.5px solid var(--rm-danger);
    border-radius: var(--rm-r-lg);
    font-family: var(--rm-font);
    font-weight: 600;
    font-size: .86rem;
    cursor: pointer;
    transition: all var(--rm-ease);
    display: flex; align-items: center; justify-content: center; gap: 8px;
    min-height: 48px;
  }
  .rm-btn-danger:hover { background: var(--rm-danger-wash); }

  /* ── Label ── */
  .rm-label {
    font-size: .65rem;
    font-weight: 700;
    color: var(--rm-muted);
    text-transform: uppercase;
    letter-spacing: .1em;
  }

  /* ── Headings ── */
  .rm-h1 {
    font-family: var(--rm-font-display);
    font-size: clamp(1.5rem, 5vw, 2rem);
    font-weight: 700;
    color: var(--rm-ink);
    line-height: 1.15;
    letter-spacing: -.02em;
    margin: 0;
  }
  .rm-h2 {
    font-family: var(--rm-font-display);
    font-size: 1.25rem;
    font-weight: 700;
    color: var(--rm-ink);
    line-height: 1.2;
    letter-spacing: -.02em;
    margin: 0;
  }
  .rm-h3 {
    font-size: .92rem;
    font-weight: 700;
    color: var(--rm-ink);
    margin: 0;
  }
  .rm-muted {
    font-size: .82rem;
    color: var(--rm-muted);
    line-height: 1.65;
  }

  /* ── Input ── */
  .rm-input {
    width: 100%;
    padding: 13px 16px;
    font-family: var(--rm-font);
    font-size: .9rem;
    font-weight: 500;
    background: var(--rm-cloud);
    color: var(--rm-ink);
    border: 1.5px solid var(--rm-sand-light);
    border-radius: var(--rm-r);
    outline: none;
    transition: border-color .25s, box-shadow .25s, background .25s;
    -webkit-appearance: none;
  }
  .rm-input::placeholder { color: var(--rm-sand); font-weight: 400; }
  .rm-input:focus {
    border-color: var(--rm-brand-light);
    background: var(--rm-card);
    box-shadow: 0 0 0 4px var(--rm-brand-wash);
  }
  .rm-textarea {
    width: 100%;
    padding: 13px 16px;
    font-family: var(--rm-font);
    font-size: .88rem;
    font-weight: 400;
    background: var(--rm-cloud);
    color: var(--rm-ink);
    border: 1.5px solid var(--rm-sand-light);
    border-radius: var(--rm-r);
    outline: none;
    resize: vertical;
    line-height: 1.6;
    transition: border-color .25s, box-shadow .25s;
    -webkit-appearance: none;
  }
  .rm-textarea::placeholder { color: var(--rm-sand); }
  .rm-textarea:focus {
    border-color: var(--rm-brand-light);
    background: var(--rm-card);
    box-shadow: 0 0 0 4px var(--rm-brand-wash);
  }

  /* ── Page Layout ── */
  .rm-page {
    background: var(--rm-bg);
    min-height: 100vh;
    min-height: 100dvh;
    position: relative;
  }
  .rm-page::before {
    content: '';
    position: fixed; inset: 0; z-index: 0; pointer-events: none;
    background: radial-gradient(ellipse at 15% 0%, var(--rm-brand-wash), transparent 50%),
                radial-gradient(ellipse at 85% 100%, var(--rm-peach-wash), transparent 50%);
  }
  .rm-content {
    position: relative; z-index: 1;
    max-width: 860px;
    margin: 0 auto;
    padding: 28px 20px 100px;
  }

  /* ── Bottom Nav (mobile) ── */
  .rm-bottom-nav {
    display: none;
    position: fixed;
    bottom: 0; left: 0; right: 0;
    z-index: 200;
    background: var(--rm-card);
    border-top: 1px solid var(--rm-sand-light);
    padding: 8px 0 calc(8px + env(safe-area-inset-bottom, 0));
    box-shadow: 0 -4px 24px rgba(45,54,77,.06);
  }
  .rm-bottom-nav-inner {
    display: flex;
    align-items: center;
    justify-content: space-around;
    max-width: 400px;
    margin: 0 auto;
  }
  .rm-tab-btn {
    flex: 1;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 3px;
    padding: 6px 8px;
    background: none;
    border: none;
    cursor: pointer;
    transition: all var(--rm-ease);
    border-radius: 12px;
    min-height: 52px;
  }
  .rm-tab-btn .rm-tab-icon {
    font-size: 1.25rem;
    line-height: 1;
    transition: transform var(--rm-spring);
  }
  .rm-tab-btn .rm-tab-label {
    font-family: var(--rm-font);
    font-size: .6rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: .06em;
    color: var(--rm-muted);
    transition: color var(--rm-ease);
  }
  .rm-tab-btn.active .rm-tab-icon { transform: scale(1.15) translateY(-2px); }
  .rm-tab-btn.active .rm-tab-label { color: var(--rm-brand); }
  .rm-tab-btn:active .rm-tab-icon { transform: scale(.9); }

  /* ── Notification Toast ── */
  .rm-toast {
    position: fixed;
    top: calc(16px + env(safe-area-inset-top, 0));
    right: 16px;
    left: 16px;
    z-index: 9999;
    max-width: 380px;
    margin: 0 auto;
    padding: 13px 18px;
    border-radius: var(--rm-r-lg);
    font-family: var(--rm-font);
    font-weight: 600;
    font-size: .84rem;
    color: #fff;
    box-shadow: 0 8px 32px rgba(0,0,0,.25);
    display: flex; align-items: center; gap: 10px;
    animation: toastIn .3s var(--rm-spring) both;
  }
  @keyframes toastIn {
    from { opacity: 0; transform: translateY(-12px) scale(.96); }
    to   { opacity: 1; transform: translateY(0) scale(1); }
  }

  /* ── Modal Overlay ── */
  .rm-overlay {
    position: fixed; inset: 0;
    background: rgba(0,0,0,.55);
    backdrop-filter: blur(4px);
    -webkit-backdrop-filter: blur(4px);
    z-index: 9000;
    display: flex; align-items: flex-end; justify-content: center;
    padding: 0;
    animation: fadeIn .2s ease;
  }
  .rm-overlay-desktop {
    align-items: center;
    padding: 24px;
  }
  @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
  .rm-modal {
    background: var(--rm-card);
    border-radius: var(--rm-r-xl) var(--rm-r-xl) 0 0;
    padding: 24px 20px calc(24px + env(safe-area-inset-bottom, 0));
    width: 100%;
    max-width: 480px;
    max-height: 85vh;
    overflow-y: auto;
    animation: slideUp .35s var(--rm-spring) both;
    border-top: 1px solid var(--rm-sand-light);
  }
  .rm-modal-desktop {
    border-radius: var(--rm-r-xl);
    padding: 32px 28px;
    animation: popIn .3s var(--rm-spring) both;
  }
  .rm-modal-handle {
    width: 36px; height: 4px;
    background: var(--rm-sand);
    border-radius: 99px;
    margin: 0 auto 20px;
  }
  @keyframes slideUp {
    from { opacity: 0; transform: translateY(30px); }
    to   { opacity: 1; transform: translateY(0); }
  }
  @keyframes popIn {
    from { opacity: 0; transform: scale(.94) translateY(10px); }
    to   { opacity: 1; transform: scale(1) translateY(0); }
  }

  /* ── Onboarding ── */
  
  .rm-ob-page {
    min-height: 100vh;
    min-height: 100dvh;
    background: var(--rm-bg);
    display: flex;
    flex-direction: column;
    position: relative;
    overflow: hidden;
  }
  .rm-ob-page::before {
    content: '';
    position: absolute; inset: 0; pointer-events: none;
    background: radial-gradient(ellipse at 20% 10%, var(--rm-brand-wash), transparent 50%),
                radial-gradient(ellipse at 80% 90%, var(--rm-peach-wash), transparent 50%);
  }
  .rm-ob-header {
    padding: calc(20px + env(safe-area-inset-top, 0px)) 20px 0;
    flex-shrink: 0;
    position: relative; z-index: 1;
  }
  .rm-ob-progress {
    display: flex; gap: 5px;
  }
  .rm-ob-bar {
    flex: 1; height: 3px; border-radius: 99px;
    background: var(--rm-sand-light);
    transition: background .35s ease;
  }
  .rm-ob-bar.on { background: var(--rm-brand); }
  .rm-ob-body {
    flex: 1;
    overflow-y: auto;
    padding: 20px 20px 20px; /* <--- Changed: Added 20px bottom padding */
    position: relative; z-index: 1;
    -webkit-overflow-scrolling: touch;
  }
  .rm-ob-footer {
    flex-shrink: 0;
    /* vvv Changed: Added + 70px to push buttons above mobile nav bar vvv */
    padding: 16px 20px calc(16px + env(safe-area-inset-bottom, 0px) + 70px);
    background: var(--rm-card);
    border-top: 1px solid var(--rm-sand-light);
    display: flex; gap: 10px;
    position: relative; z-index: 10; /* <--- Changed: Increased z-index to 10 */
    box-shadow: 0 -4px 20px rgba(45,54,77,.05);
  }

  /* ── Category / Addiction Cards ── */
  .rm-select-card {
    background: var(--rm-card);
    border: 2px solid var(--rm-sand-light);
    border-radius: var(--rm-r-lg);
    padding: 18px 16px;
    cursor: pointer;
    transition: all var(--rm-ease);
    box-shadow: var(--rm-sh-sm);
    -webkit-tap-highlight-color: transparent;
  }
  .rm-select-card:active { transform: scale(.97); }
  .rm-select-card.selected {
    border-color: var(--rm-brand);
    background: var(--rm-brand-wash);
    box-shadow: 0 0 0 4px var(--rm-brand-wash), var(--rm-sh-sm);
  }
  .rm-select-row {
    background: var(--rm-card);
    border: 2px solid var(--rm-sand-light);
    border-radius: var(--rm-r-lg);
    padding: 16px;
    cursor: pointer;
    transition: all var(--rm-ease);
    display: flex; align-items: center; gap: 14px;
    box-shadow: var(--rm-sh-sm);
    -webkit-tap-highlight-color: transparent;
  }
  .rm-select-row:active { transform: scale(.98); }
  .rm-select-row.selected {
    border-color: var(--rm-brand);
    background: var(--rm-brand-wash);
  }

  /* ── Stats Grid ── */
  .rm-stats-grid {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 10px;
  }
  .rm-stat-card {
    background: var(--rm-card);
    border: 1px solid var(--rm-sand-light);
    border-radius: var(--rm-r-lg);
    padding: 16px 10px;
    text-align: center;
    box-shadow: var(--rm-sh-sm);
    transition: transform var(--rm-ease);
  }
  .rm-stat-card:hover { transform: translateY(-2px); }
  .rm-stat-value {
    font-family: var(--rm-font-display);
    font-size: clamp(1.2rem, 2.5vw, 1.6rem);
    font-weight: 700;
    line-height: 1;
    letter-spacing: -.02em;
  }
  .rm-stat-label {
    font-size: .62rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: .07em;
    color: var(--rm-muted);
    margin-top: 5px;
  }

  /* ── Today Card ── */
  .rm-today-card {
    background: var(--rm-card);
    border-radius: var(--rm-r-xl);
    padding: 22px 20px;
    cursor: pointer;
    transition: all var(--rm-ease);
    position: relative; overflow: hidden;
    box-shadow: var(--rm-sh-md);
    border: 1px solid var(--rm-sand-light);
    -webkit-tap-highlight-color: transparent;
  }
  .rm-today-card:active { transform: scale(.985); }
  .rm-today-card:hover { transform: translateY(-2px); box-shadow: var(--rm-sh-lg); }
  .rm-today-accent {
    position: absolute; top: 0; left: 0; right: 0; height: 3px;
  }

  /* ── Journey Grid ── */
  .rm-day-grid {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
  }
  .rm-day-dot {
    width: 36px; height: 36px;
    border-radius: 10px;
    display: flex; align-items: center; justify-content: center;
    font-size: 11px; font-weight: 700;
    font-family: var(--rm-font);
    transition: all var(--rm-ease);
    border: 2px solid transparent;
    -webkit-tap-highlight-color: transparent;
    flex-shrink: 0;
  }
  .rm-day-dot:not([data-future="true"]) { cursor: pointer; }
  .rm-day-dot:not([data-future="true"]):active { transform: scale(.87); }

  /* ── Phase Legend ── */
  .rm-phase-legend {
    display: flex;
    gap: 12px;
    flex-wrap: wrap;
    margin-top: 14px;
  }
  .rm-phase-dot {
    display: flex; align-items: center; gap: 5px;
    font-family: var(--rm-font);
    font-size: .68rem;
    color: var(--rm-muted);
    font-weight: 500;
  }

  /* ── Quick Actions ── */
  .rm-quick-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 10px;
  }
  .rm-quick-btn {
    background: var(--rm-card);
    border: 1px solid var(--rm-sand-light);
    border-radius: var(--rm-r-lg);
    padding: 18px 14px;
    cursor: pointer;
    text-align: center;
    transition: all var(--rm-ease);
    font-family: var(--rm-font);
    box-shadow: var(--rm-sh-sm);
    -webkit-tap-highlight-color: transparent;
    min-height: 90px;
    display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 6px;
  }
  .rm-quick-btn:hover { border-color: var(--rm-brand-light); background: var(--rm-brand-wash); }
  .rm-quick-btn:active { transform: scale(.96); }

  /* ── Badge Items ── */
  .rm-badge-item {
    background: var(--rm-card);
    border: 1px solid var(--rm-sand-light);
    border-radius: var(--rm-r-lg);
    padding: 14px 16px;
    display: flex; align-items: center; gap: 13px;
    box-shadow: var(--rm-sh-sm);
    transition: all var(--rm-ease);
  }
  .rm-badge-item.earned {
    border-color: rgba(90,105,147,.25);
    background: var(--rm-brand-wash);
  }
  .rm-badge-item:not(.earned) { opacity: .38; }

  /* ── Why Banner ── */
  .rm-why-banner {
    border-left: 4px solid var(--rm-brand);
    border-radius: 0 var(--rm-r) var(--rm-r) 0;
    padding: 14px 18px;
    margin-bottom: 14px;
  }

  /* ── Progress Bar ── */
  .rm-progress-track {
    height: 8px;
    background: var(--rm-sand-light);
    border-radius: 99px;
    overflow: hidden;
  }
  .rm-progress-fill {
    height: 100%;
    border-radius: 99px;
    transition: width .6s ease;
  }

  /* ── Scrollable ── */
  .rm-scroll-x {
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
    scrollbar-width: none;
  }
  .rm-scroll-x::-webkit-scrollbar { display: none; }

  /* ── Section ── */
  .rm-section {
    background: var(--rm-card);
    border: 1px solid var(--rm-sand-light);
    border-radius: var(--rm-r-xl);
    padding: 20px;
    box-shadow: var(--rm-sh-sm);
  }

  /* ── Day View Sections ── */
  .rm-day-section {
    background: var(--rm-card);
    border: 1px solid var(--rm-sand-light);
    border-radius: var(--rm-r-lg);
    padding: 18px;
    box-shadow: var(--rm-sh-sm);
  }
  .rm-day-section-header {
    display: flex; align-items: center; gap: 10px;
    margin-bottom: 12px;
  }
  .rm-day-section-icon {
    width: 36px; height: 36px; border-radius: 10px;
    background: var(--rm-surface);
    border: 1px solid var(--rm-sand-light);
    display: flex; align-items: center; justify-content: center;
    font-size: .95rem; flex-shrink: 0;
  }

  /* ── Action Row (day view bottom) ── */
  .rm-action-bar {
    position: sticky;
    bottom: 0;
    background: var(--rm-card);
    border-top: 1px solid var(--rm-sand-light);
    padding: 12px 16px calc(12px + env(safe-area-inset-bottom, 0));
    display: flex; flex-direction: column; gap: 8px;
    box-shadow: 0 -6px 24px rgba(45,54,77,.07);
  }

  /* ── Severity Buttons ── */
  .rm-sev-btn {
    flex: 1;
    padding: 12px 8px;
    border-radius: var(--rm-r);
    font-family: var(--rm-font);
    font-weight: 600;
    font-size: .82rem;
    cursor: pointer;
    transition: all var(--rm-ease);
    border: 2px solid transparent;
    text-align: center;
    min-height: 48px;
  }
  .rm-sev-btn.active { transform: scale(1.02); }
  .rm-sev-btn:active { transform: scale(.96); }

  /* ── Trigger Pill ── */
  .rm-trigger-pill {
    padding: 9px 16px;
    border-radius: 99px;
    font-family: var(--rm-font);
    font-size: .8rem;
    font-weight: 500;
    cursor: pointer;
    transition: all var(--rm-ease);
    border: 1.5px solid var(--rm-sand-light);
    background: var(--rm-card);
    color: var(--rm-muted);
    min-height: 40px;
    display: flex; align-items: center;
    -webkit-tap-highlight-color: transparent;
  }
  .rm-trigger-pill:active { transform: scale(.94); }
  .rm-trigger-pill.selected {
    border-color: var(--rm-brand);
    background: var(--rm-brand-wash);
    color: var(--rm-brand);
    font-weight: 700;
  }

  /* ── Time Input ── */
  input[type="time"].rm-input {
    color-scheme: auto;
  }

  /* ── Withdrawal Warning ── */
  .rm-withdrawal {
    background: var(--rm-danger-wash);
    border: 1px solid rgba(220,38,38,.18);
    border-radius: var(--rm-r-lg);
    padding: 14px 16px;
  }

  /* ── Completed Banner ── */
  .rm-completed-banner {
    background: var(--rm-sage-wash);
    border: 1px solid rgba(123,143,138,.2);
    border-radius: var(--rm-r-lg);
    padding: 20px;
    text-align: center;
  }

  /* ── Settings Row ── */
  .rm-settings-row {
    display: flex; justify-content: space-between; align-items: center;
    padding: 12px 0;
    border-bottom: 1px solid var(--rm-sand-light);
  }
  .rm-settings-row:last-child { border-bottom: none; }

  /* ── Danger Zone ── */
  .rm-danger-zone {
    background: var(--rm-danger-wash);
    border: 1px solid rgba(220,38,38,.2);
    border-radius: var(--rm-r-xl);
    padding: 20px;
  }

  /* ── Spinner ── */
  .rm-spin {
    width: 15px; height: 15px;
    border: 2px solid rgba(255,255,255,.3);
    border-top-color: #fff;
    border-radius: 50%;
    animation: spin .6s linear infinite;
    display: inline-block;
    flex-shrink: 0;
  }
  @keyframes spin { to { transform: rotate(360deg); } }

  /* ── Loading ── */
  .rm-loading-pulse {
    animation: loadPulse 1.8s ease-in-out infinite;
  }
  @keyframes loadPulse {
    0%, 100% { opacity: .4; transform: scale(.95); }
    50%       { opacity: 1;  transform: scale(1);    }
  }

  /* ── Confetti ── */
  @keyframes confettiFall {
    0%   { transform: translateY(-20px) rotate(0deg); opacity: 1; }
    100% { transform: translateY(100vh) rotate(720deg); opacity: 0; }
  }

  /* ── Upgrade Modal ── */
  .rm-upgrade-highlight {
    border-radius: var(--rm-r-lg);
    padding: 16px;
    margin-bottom: 20px;
  }

  
/* ── Mobile Responsive ── */
  @media (max-width: 640px) {
    .rm-bottom-nav { display: block; }
    .rm-content { padding: 16px 14px 90px; }
    .rm-stats-grid { grid-template-columns: repeat(2, 1fr); gap: 8px; }
    .rm-stat-card { padding: 14px 8px; }
    .rm-ob-body { padding: 16px 16px 20px; } /* <--- Changed: Added 20px bottom padding */
    .rm-ob-header { padding: calc(16px + env(safe-area-inset-top, 0px)) 16px 0; }
    /* vvv Changed: Added + 70px to push buttons up on mobile vvv */
    .rm-ob-footer { padding: 12px 16px calc(12px + env(safe-area-inset-bottom, 0px) + 70px); }
    .rm-section { padding: 16px; border-radius: var(--rm-r-lg); }
    .rm-day-section { padding: 14px; }
    .rm-overlay { align-items: flex-end; }
    .rm-modal { max-height: 90vh; }
    .rm-h1 { font-size: 1.6rem; }
  }
  @media (max-width: 380px) {
    .rm-stats-grid { grid-template-columns: repeat(2, 1fr); }
    .rm-ob-body { padding: 12px 14px 0; }
    .rm-day-dot { width: 32px; height: 32px; font-size: 10px; border-radius: 8px; }
  }

  /* ── PWA standalone ── */
  @media (display-mode: standalone) {
    .rm-ob-header { padding-top: calc(24px + env(safe-area-inset-top, 0)); }
    .rm-bottom-nav { padding-bottom: calc(10px + env(safe-area-inset-bottom, 0)); }
  }

  /* ── Slide animation ── */
  @keyframes slideIn { from { opacity: 0; transform: translateY(16px); } to { opacity: 1; transform: translateY(0); } }
  .rm-animate-in { animation: slideIn .4s var(--rm-spring) both; }
`;

// ─── PHASE CONFIG ─────────────────────────────────────────────────────────────
const PHASE_STYLES = {
  "Awareness":          { color: "#5A6993", wash: "rgba(90,105,147,.08)",  icon: "🌱" },
  "Rewiring":           { color: "#7B8F8A", wash: "rgba(123,143,138,.09)", icon: "🔄" },
  "Freedom":            { color: "#C4845A", wash: "rgba(196,132,90,.09)",  icon: "🕊️" },
  "Deep Integration":   { color: "#323B52", wash: "rgba(50,59,82,.08)",    icon: "🧠" },
  "Mastery":            { color: "#8a7b9b", wash: "rgba(138,123,155,.09)", icon: "⭐" },
  "Legacy":             { color: "#6B8F6B", wash: "rgba(107,143,107,.09)", icon: "🌳" },
};
const getPhase = (name) => PHASE_STYLES[name] || PHASE_STYLES["Awareness"];

// ─── SHARED ICON BUTTON ───────────────────────────────────────────────────────
const IconBtn = ({ onClick, children, style }) => (
  <button onClick={onClick} style={{
    width: 40, height: 40, borderRadius: 12,
    border: "1.5px solid var(--rm-sand-light)",
    background: "var(--rm-card)",
    color: "var(--rm-muted)",
    cursor: "pointer", display: "flex",
    alignItems: "center", justifyContent: "center",
    fontSize: "1rem", flexShrink: 0,
    boxShadow: "var(--rm-sh-sm)",
    transition: "all .2s",
    fontFamily: "var(--rm-font)",
    ...style,
  }}>
    {children}
  </button>
);

// ─── MAIN COMPONENT ────────────────────────────────────────────────────────────
export default function Roadmap({ isEmbeddedFullscreen }) {
  const navigate = useNavigate();

  const [theme, setTheme] = useState(localStorage.getItem("theme") || "light-theme");
  const [showInsightMenu, setShowInsightMenu] = useState(false);
  const hideTimeout = useRef(null);

  useEffect(() => { document.body.className = theme; }, [theme]);

  const toggleTheme = () => {
    const themes = ["light-theme", "dark-theme", "zen-theme"];
    const next = themes[(themes.indexOf(theme) + 1) % themes.length];
    setTheme(next);
    localStorage.setItem("theme", next);
    document.body.className = next;
  };

  const handleLogout = async () => { await signOut(auth); navigate("/"); };
  const handleInsightEnter = () => { clearTimeout(hideTimeout.current); setShowInsightMenu(true); };
  const handleInsightLeave = () => { hideTimeout.current = setTimeout(() => setShowInsightMenu(false), 150); };

  const [view,      setView]      = useState("loading");
  const [step,      setStep]      = useState(0);
  const [config,    setConfig]    = useState(null);
  const [stats,     setStats]     = useState(null);
  const [todayData, setTodayData] = useState(null);
  const [selectedDay, setSelectedDay]   = useState(null);
  const [notification, setNotification] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showUpgrade, setShowUpgrade] = useState(false);
  const [confetti,  setConfetti]  = useState(false);

  const [ob, setOb] = useState({
    category: null, addiction: null, planLength: null,
    severity: null, triggers: null,
    name: "", why: "", wakeTime: "07:00", sleepTime: "23:00",
  });

  const refreshStats = useCallback(() => {
    if (hasActiveRoadmap()) {
      setConfig(getConfig());
      setStats(getStats());
      setTodayData(getTodayData());
    }
  }, []);

  useEffect(() => {
    if (hasActiveRoadmap()) { refreshStats(); setView("dashboard"); }
    else setView("onboarding");
  }, []);

  const notify = (msg, type = "success") => {
    setNotification({ msg, type });
    setTimeout(() => setNotification(null), 3500);
  };

  useEffect(() => {
    if (stats?.completedDays === 21 && stats?.config?.planLength === 21) setShowUpgrade(true);
  }, [stats]);

  const openDay = (day) => { setSelectedDay(day); setView("day"); };

  // Bottom tab navigation
  const tabs = [
    { id: "dashboard", icon: "🏠", label: "Home"    },
    { id: "today",     icon: "📅", label: "Today"   },
    { id: "badges",    icon: "🏅", label: "Badges"  },
    { id: "settings",  icon: "⚙️", label: "Settings" },
  ];

  const handleTabPress = (tabId) => {
    if (tabId === "today") { openDay(getTodayDay()); return; }
    if (tabId === "badges") { setView("badges"); return; }
    if (tabId === "settings") { setView("settings"); return; }
    setView("dashboard");
  };

  const activeTab = view === "day" ? "today"
    : view === "badges" ? "badges"
    : view === "settings" ? "settings"
    : "dashboard";

  const toastBg = (type) =>
    type === "success" ? "#7B8F8A"
    : type === "warning" ? "#C4845A"
    : "#dc2626";

  return (
    <div className="rm-root">
      <style>{rmCss}</style>
      <div className="rm-page">

        {/* ── Navbar (desktop only) ── */}
        {!isEmbeddedFullscreen && (
          <nav className="navbar">
            <div className="navbar-left">
              <Link to="/dashboard" className="nav-item">Home</Link>
              <Link to="/resources" className="nav-item">Resources</Link>
              <Link to="/community">Community</Link>
              <Link to="/feed" className="nav-item">Feed</Link>
              <Link to="/chatbot">Chat with Saksham</Link>
              <div className="nav-dropdown" onMouseEnter={handleInsightEnter} onMouseLeave={handleInsightLeave}>
                <span className="nav-item">Insight ▾</span>
                {showInsightMenu && (
                  <div className="dropdown-menu">
                    <Link to="/insight" className="dropdown-link">See Saved Diary</Link>
                    <Link to="/insight" className="dropdown-link">Look at Report</Link>
                  </div>
                )}
              </div>
              <Link to="/chattr" className="nav-item">Chattr</Link>
            </div>
            <div className="navbar-center">
              <span className="healhub-brand"><span className="hug-icon">💖🌿</span> HealHub</span>
              <Link to="/about" className="nav-item">About Us</Link>
            </div>
            <div className="navbar-right">
              <button onClick={toggleTheme} className="theme-switcher-btn">
                {theme === "light-theme" ? <MdOutlineLightMode /> : theme === "dark-theme" ? <MdModeNight /> : <MdFilterVintage />}
              </button>
              <Link to="/profile" className="profile-icon-link"><FaUserCircle size={24} /></Link>
              <button onClick={handleLogout} className="logout-btn">Log Out</button>
            </div>
          </nav>
        )}

        {/* ── Toast Notification ── */}
        {notification && (
          <div className="rm-toast" style={{ background: toastBg(notification.type) }}>
            <span style={{ fontSize: "1.1rem" }}>
              {notification.type === "success" ? "✅" : notification.type === "warning" ? "💙" : "⚠️"}
            </span>
            {notification.msg}
          </div>
        )}

        {confetti && <ConfettiOverlay onDone={() => setConfetti(false)} />}

        {/* ── Upgrade Modal ── */}
        {showUpgrade && (
          <UpgradeModal
            onUpgrade={() => { upgrade60Day(); refreshStats(); setShowUpgrade(false); notify("🎉 Upgraded to 60-Day Foundation!"); }}
            onDecline={() => setShowUpgrade(false)}
          />
        )}

        {/* ── Delete Confirm ── */}
        {showDeleteConfirm && (
          <ConfirmModal
            title="Delete Your Roadmap?"
            message="This will permanently delete your progress, streak, and badges. This cannot be undone."
            confirmLabel="Yes, Delete Everything"
            danger
            onConfirm={async () => {
              await deleteRoadmap();
              setShowDeleteConfirm(false);
              setView("onboarding"); setStep(0);
              setOb({ category:null, addiction:null, planLength:null, severity:null, triggers:null, name:"", why:"", wakeTime:"07:00", sleepTime:"23:00" });
            }}
            onCancel={() => setShowDeleteConfirm(false)}
          />
        )}

        {/* ── Views ── */}
        {view === "loading"    && <LoadingView />}
        {view === "onboarding" && (
          <OnboardingView
            ob={ob} setOb={setOb} step={step} setStep={setStep}
            onComplete={async (finalOb) => {
              await createRoadmap({
                addiction: finalOb.addiction, planLength: finalOb.planLength,
                severity: finalOb.severity, name: finalOb.name, why: finalOb.why,
                wakeTime: finalOb.wakeTime, sleepTime: finalOb.sleepTime, triggerType: finalOb.triggers,
              });
              refreshStats(); setView("dashboard");
              notify("🌱 Your recovery journey begins today.");
            }}
          />
        )}
        {view === "dashboard" && (
          <DashboardView
            config={config} stats={stats} todayData={todayData}
            onOpenDay={openDay} onViewBadges={() => setView("badges")}
            onSettings={() => setView("settings")}
            onLogUrge={() => {
              const r = logUrge(); refreshStats();
              if (r.newBadge) notify("🛡️ Badge Unlocked: Urge Warrior!", "success");
              else notify("Urge logged. You're stronger than this.");
            }}
          />
        )}
        {view === "day" && (
          <DayView
            dayNumber={selectedDay} config={config}
            onBack={() => { setView("dashboard"); refreshStats(); }}
            onComplete={(reflection) => {
              const r = completeDay(selectedDay, reflection);
              refreshStats(); setView("dashboard"); setConfetti(true);
              if (r?.newBadges?.length > 0) {
                const badge = ALL_BADGES.find(b => b.id === r.newBadges[0]);
                notify(`${badge?.emoji ?? "🏅"} Badge: ${badge?.label ?? "Unlocked"}!`);
              } else notify("✅ Day complete! Keep going.");
            }}
            onStruggled={() => { markStruggled(selectedDay); notify("💙 Logged. Honesty is part of healing.", "warning"); }}
            onRelapse={() => { handleRelapse(selectedDay); refreshStats(); setView("dashboard"); notify("💛 Setback noted. Back 2 days. Keep going.", "warning"); }}
          />
        )}
        {view === "badges"   && <BadgesView badges={getBadges()} onBack={() => setView("dashboard")} />}
        {view === "settings" && (
          <SettingsView config={config} onBack={() => setView("dashboard")} onDelete={() => setShowDeleteConfirm(true)} />
        )}

        {/* ── Bottom Tab Bar (mobile) ── */}
        {view !== "loading" && view !== "onboarding" && (
          <nav className="rm-bottom-nav">
            <div className="rm-bottom-nav-inner">
              {tabs.map(tab => (
                <button key={tab.id} className={`rm-tab-btn ${activeTab === tab.id ? "active" : ""}`} onClick={() => handleTabPress(tab.id)}>
                  <span className="rm-tab-icon">{tab.icon}</span>
                  <span className="rm-tab-label" style={{ color: activeTab === tab.id ? "var(--rm-brand)" : undefined }}>
                    {tab.label}
                  </span>
                </button>
              ))}
            </div>
          </nav>
        )}

      </div>
    </div>
  );
}

// ─── LOADING ──────────────────────────────────────────────────────────────────
function LoadingView() {
  return (
    <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100vh", background: "var(--rm-bg)" }}>
      <div style={{ textAlign: "center" }}>
        <div className="rm-loading-pulse" style={{ fontSize: 52, marginBottom: 16 }}>🌱</div>
        <div style={{ fontFamily: "var(--rm-font-display)", fontSize: "1rem", color: "var(--rm-muted)", fontStyle: "italic" }}>
          Loading your journey...
        </div>
      </div>
    </div>
  );
}

// ─── ONBOARDING ───────────────────────────────────────────────────────────────
function OnboardingView({ ob, setOb, step, setStep, onComplete }) {
  const [localName, setLocalName] = useState(ob.name);
  const [localWhy,  setLocalWhy]  = useState(ob.why);
  const STEPS = ["category", "addiction", "plan", "assess", "personalize"];

  const canProceed = () => {
    if (step === 0) return !!ob.category;
    if (step === 1) return !!ob.addiction;
    if (step === 2) return !!ob.planLength;
    if (step === 3) return !!ob.severity && !!ob.triggers;
    if (step === 4) return localName.trim().length > 0 && localWhy.trim().length > 10;
    return false;
  };

  const next = () => {
    if (step === 4) onComplete({ ...ob, name: localName.trim(), why: localWhy.trim() });
    else setStep(s => s + 1);
  };

  const selectedCategory = CATEGORIES.find(c => c.id === ob.category);
  const triggerOptions   = ob.addiction ? (TRIGGERS[ob.addiction] || []) : [];
  const withdrawalInfo   = ob.addiction ? WITHDRAWAL_INFO[ob.addiction] : null;

  const stepTitles = [
    { label: "Recovery Roadmap", title: "What are you\ndealing with?", sub: "Choose the category that best describes your struggle. Every journey is valid." },
    { label: selectedCategory?.label || "", title: "Which one\nspecifically?", sub: "Being specific matters. The more precisely you name it, the more targeted your plan." },
    { label: "Choose Your Plan", title: "How deep do you\nwant to go?", sub: "Both plans are serious. Choose based on your current readiness." },
    { label: "Personalization", title: "Tell us about\nyour situation", sub: "Be honest. This makes your plan specific to you, not generic." },
    { label: "Almost There", title: "Make it\npersonal", sub: "These two answers will appear every single day — the foundation of your recovery." },
  ];
  const current = stepTitles[step];

  return (
    <div className="rm-ob-page">
      {/* Header */}
      <div className="rm-ob-header">
        <div className="rm-ob-progress">
          {STEPS.map((_, i) => (
            <div key={i} className={`rm-ob-bar ${i <= step ? "on" : ""}`} />
          ))}
        </div>
        <div style={{ marginTop: 10, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <span className="rm-label" style={{ color: "var(--rm-brand)" }}>Step {step + 1} of {STEPS.length}</span>
          <span className="rm-label">{current.label}</span>
        </div>
      </div>

      {/* Body */}
      <div className="rm-ob-body">
        <div style={{ marginBottom: 24, paddingTop: 4 }}>
          <div className="rm-h1" style={{ whiteSpace: "pre-line", marginBottom: 8 }}>{current.title}</div>
          <p className="rm-muted">{current.sub}</p>
        </div>

        {/* Step 0 — Category */}
        {step === 0 && (
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
            {CATEGORIES.map(cat => (
              <div key={cat.id} className={`rm-select-card ${ob.category === cat.id ? "selected" : ""}`}
                onClick={() => setOb(o => ({ ...o, category: cat.id, addiction: null }))}>
                <div style={{ fontSize: "2rem", marginBottom: 8 }}>{cat.emoji}</div>
                <div style={{ fontFamily: "var(--rm-font)", fontWeight: 700, fontSize: ".88rem", color: "var(--rm-ink)", marginBottom: 4 }}>{cat.label}</div>
                <div style={{ fontSize: ".75rem", color: "var(--rm-muted)", lineHeight: 1.5 }}>{cat.description}</div>
                {ob.category === cat.id && (
                  <div style={{ marginTop: 8, color: "var(--rm-brand)", fontSize: ".75rem", fontWeight: 700 }}>✓ Selected</div>
                )}
              </div>
            ))}
          </div>
        )}

        {/* Step 1 — Addiction */}
        {step === 1 && selectedCategory && (
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {selectedCategory.addictions.map(add => (
              <div key={add.id} className={`rm-select-row ${ob.addiction === add.id ? "selected" : ""}`}
                onClick={() => setOb(o => ({ ...o, addiction: add.id }))}>
                <span style={{ fontSize: "1.5rem", flexShrink: 0 }}>{add.emoji}</span>
                <div style={{ flex: 1 }}>
                  <div style={{ fontFamily: "var(--rm-font)", fontWeight: 700, color: "var(--rm-ink)", fontSize: ".9rem" }}>{add.label}</div>
                  <div style={{ fontSize: ".75rem", color: "var(--rm-muted)", marginTop: 2 }}>{add.sublabel}</div>
                </div>
                {ob.addiction === add.id && (
                  <div style={{ color: "var(--rm-brand)", fontWeight: 700, fontSize: "1.1rem", flexShrink: 0 }}>✓</div>
                )}
              </div>
            ))}
          </div>
        )}

        {/* Step 2 — Plan Length */}
        {step === 2 && (
          <div>
            {withdrawalInfo && (
              <div className="rm-withdrawal" style={{ marginBottom: 14 }}>
                <div style={{ fontWeight: 700, color: "var(--rm-danger)", marginBottom: 6, fontSize: ".8rem" }}>
                  ⚠️ Heads up for your first {withdrawalInfo.peak}
                </div>
                <div style={{ fontSize: ".78rem", color: "var(--rm-muted)", lineHeight: 1.6 }}>
                  You may experience: {withdrawalInfo.symptoms.join(" · ")}. These are signs of healing.
                </div>
              </div>
            )}
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
              {[
                { length: 21, icon: "🌱", label: "21-Day Kickstart", sublabel: "Break the Pattern", desc: "Build awareness, reduce, and begin rewiring. Perfect for starting.", tag: "Recommended" },
                { length: 60, icon: "🏆", label: "60-Day Foundation", sublabel: "Rewire Your Brain", desc: "Complete neurological rewiring across 4 phases. Total commitment.", tag: "Deep Transformation" },
              ].map(plan => (
                <div key={plan.length} className={`rm-select-card ${ob.planLength === plan.length ? "selected" : ""}`}
                  onClick={() => setOb(o => ({ ...o, planLength: plan.length }))}>
                  <div style={{ fontSize: "2rem", marginBottom: 8 }}>{plan.icon}</div>
                  <div style={{ fontSize: ".65rem", fontWeight: 700, color: "var(--rm-brand)", textTransform: "uppercase", letterSpacing: ".08em", marginBottom: 4 }}>{plan.tag}</div>
                  <div style={{ fontFamily: "var(--rm-font-display)", fontWeight: 700, fontSize: "1rem", color: "var(--rm-ink)", marginBottom: 3 }}>{plan.label}</div>
                  <div style={{ fontSize: ".78rem", color: "var(--rm-brand)", fontWeight: 600, marginBottom: 8 }}>{plan.sublabel}</div>
                  <div style={{ fontSize: ".75rem", color: "var(--rm-muted)", lineHeight: 1.5 }}>{plan.desc}</div>
                  <div style={{ marginTop: 14, fontFamily: "var(--rm-font-display)", fontWeight: 700, fontSize: "1.5rem", color: ob.planLength === plan.length ? "var(--rm-brand)" : "var(--rm-sand)" }}>
                    {plan.length}<span style={{ fontSize: ".75rem", fontWeight: 400, fontFamily: "var(--rm-font)" }}> days</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Step 3 — Assess */}
        {step === 3 && (
          <div>
            <div style={{ marginBottom: 20 }}>
              <div className="rm-label" style={{ marginBottom: 10 }}>How severe is it for you?</div>
              <div style={{ display: "flex", gap: 8 }}>
                {[
                  { val: "mild",     label: "🟢 Mild",     bg: "rgba(123,143,138,.15)", border: "#7B8F8A", color: "#7B8F8A" },
                  { val: "moderate", label: "🟡 Moderate", bg: "rgba(230,161,132,.15)", border: "#E6A184", color: "#C4845A" },
                  { val: "severe",   label: "🔴 Severe",   bg: "rgba(220,38,38,.1)",   border: "#dc2626", color: "#dc2626" },
                ].map(s => (
                  <button key={s.val} className="rm-sev-btn"
                    style={{
                      background: ob.severity === s.val ? s.bg : "var(--rm-card)",
                      borderColor: ob.severity === s.val ? s.border : "var(--rm-sand-light)",
                      color: ob.severity === s.val ? s.color : "var(--rm-muted)",
                      opacity: ob.severity && ob.severity !== s.val ? .5 : 1,
                    }}
                    onClick={() => setOb(o => ({ ...o, severity: s.val }))}>
                    {s.label}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <div className="rm-label" style={{ marginBottom: 10 }}>What most often triggers it?</div>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 7 }}>
                {triggerOptions.map(t => (
                  <button key={t} className={`rm-trigger-pill ${ob.triggers === t ? "selected" : ""}`}
                    onClick={() => setOb(o => ({ ...o, triggers: t }))}>
                    {t}
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Step 4 — Personalize */}
        {step === 4 && (
          <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
            <div>
              <label className="rm-label" style={{ display: "block", marginBottom: 6 }}>Your first name</label>
              <input className="rm-input" value={localName} onChange={e => setLocalName(e.target.value)}
                placeholder="What should we call you?" />
            </div>
            <div>
              <label className="rm-label" style={{ display: "block", marginBottom: 6 }}>Why are you doing this?</label>
              <textarea className="rm-textarea" value={localWhy} onChange={e => setLocalWhy(e.target.value)}
                placeholder="Write honestly. Not what sounds good — what is actually true for you. This will appear every day."
                rows={4} />
              <div style={{ marginTop: 4, fontSize: ".72rem", color: "var(--rm-muted)" }}>
                {localWhy.length < 10 ? `${10 - localWhy.length} more chars needed` : "✓ Looks good"}
              </div>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
              <div>
                <label className="rm-label" style={{ display: "block", marginBottom: 6 }}>Wake up</label>
                <input type="time" className="rm-input" value={ob.wakeTime}
                  onChange={e => setOb(o => ({ ...o, wakeTime: e.target.value }))} />
              </div>
              <div>
                <label className="rm-label" style={{ display: "block", marginBottom: 6 }}>Sleep time</label>
                <input type="time" className="rm-input" value={ob.sleepTime}
                  onChange={e => setOb(o => ({ ...o, sleepTime: e.target.value }))} />
              </div>
            </div>
            <div style={{ height: 20 }} />
          </div>
        )}
      </div>

      {/* Footer */}
 {/* Footer */}
      <div className="rm-ob-footer">
        <button className="rm-btn-ghost"
          style={{ flex: step === 0 ? 0 : 1, width: step === 0 ? 48 : undefined, padding: step === 0 ? "13px" : undefined, visibility: step === 0 ? "hidden" : "visible" }}
          onClick={() => step > 0 && setStep(s => s - 1)}>
          ←
        </button>
        
        {/* Fixed: Only ONE primary button here */}
        <button className="rm-btn-primary" style={{ flex: 2 }} onClick={next} disabled={!canProceed()}>
          {step === 4 ? "Start My Journey 🌱" : "Continue →"}
        </button>
      </div>
    </div>
  );
}

// ─── DASHBOARD ────────────────────────────────────────────────────────────────
function DashboardView({ config, stats, todayData, onOpenDay, onViewBadges, onSettings, onLogUrge }) {
  const [hoursDisplay, setHoursDisplay] = useState(getHoursFree());
  const addictionMeta = config ? getAddictionMeta(config.addiction) : null;
  const progress     = getProgress();
  const days         = getPlanDays();
  const today        = getTodayDay();
  const todayUrges   = getTodayUrgeCount();

  useEffect(() => {
    const iv = setInterval(() => setHoursDisplay(getHoursFree()), 60000);
    return () => clearInterval(iv);
  }, []);

  if (!config || !stats) return <LoadingView />;

  const ph = getPhase(stats.currentPhase);
  const earnedBadges  = getBadges();
  const completedCount = Object.values(progress).filter(d => d.completed).length;

  return (
    <div className="rm-content rm-animate-in">

      {/* ── Header Card ── */}
      <div className="rm-card-elevated" style={{ padding: "20px", marginBottom: 14, position: "relative", overflow: "hidden" }}>
        <div style={{ position: "absolute", top: 0, left: 0, right: 0, height: 3, background: `linear-gradient(90deg, ${ph.color}, ${ph.color}88)` }} />
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
            <div style={{
              width: 50, height: 50, borderRadius: "50%",
              background: ph.wash,
              border: `2px solid ${ph.color}40`,
              display: "flex", alignItems: "center", justifyContent: "center",
              fontSize: "1.4rem", flexShrink: 0,
            }}>
              {addictionMeta?.emoji || ph.icon}
            </div>
            <div>
              <div className="rm-label" style={{ color: ph.color, marginBottom: 3 }}>
                {stats.currentPhase} · {config.planLength}-Day Plan
              </div>
              <div className="rm-h2" style={{ fontSize: "clamp(1.1rem, 4vw, 1.4rem)" }}>
                Hi, {config.name} 👋
              </div>
              <div style={{ fontSize: ".75rem", color: "var(--rm-muted)", marginTop: 2 }}>
                {addictionMeta?.label}
              </div>
            </div>
          </div>
          <div style={{ display: "flex", gap: 6 }}>
            <IconBtn onClick={onViewBadges}><span style={{ fontSize: ".9rem" }}>🏅</span></IconBtn>
            <IconBtn onClick={onSettings}><span style={{ fontSize: ".9rem" }}>⚙️</span></IconBtn>
          </div>
        </div>
      </div>

      {/* ── Why Banner ── */}
      <div className="rm-why-banner" style={{ background: ph.wash, borderLeftColor: ph.color, marginBottom: 14 }}>
        <div className="rm-label" style={{ color: ph.color, marginBottom: 5 }}>Why you're doing this</div>
        <div style={{ fontSize: ".88rem", color: "var(--rm-ink)", lineHeight: 1.7, fontStyle: "italic" }}>
          "{config.why}"
        </div>
      </div>

      {/* ── Stats ── */}
      <div className="rm-stats-grid" style={{ marginBottom: 14 }}>
        {[
          { label: "Hours Free",  value: hoursDisplay.toLocaleString(), color: ph.color,          icon: "⏱" },
          { label: "Day Streak",  value: stats.currentStreak,           color: "var(--rm-peach)",  icon: "🔥" },
          { label: "Days Done",   value: `${completedCount}/${config.planLength}`, color: "var(--rm-sage)", icon: "✅" },
          { label: "Urges Today", value: todayUrges,                    color: "var(--rm-danger)", icon: "🌊" },
        ].map(s => (
          <div key={s.label} className="rm-stat-card">
            <div style={{ fontSize: "1rem", marginBottom: 4 }}>{s.icon}</div>
            <div className="rm-stat-value" style={{ color: s.color }}>{s.value}</div>
            <div className="rm-stat-label">{s.label}</div>
          </div>
        ))}
      </div>

      {/* ── Progress ── */}
      <div className="rm-section" style={{ marginBottom: 14 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
          <div>
            <div className="rm-h3">Overall Progress</div>
            <div style={{ fontSize: ".75rem", color: ph.color, marginTop: 2, fontWeight: 600 }}>Phase: {stats.currentPhase}</div>
          </div>
          <div className="rm-h2" style={{ color: ph.color, fontSize: "1.5rem" }}>{stats.progressPercent}%</div>
        </div>
        <div className="rm-progress-track">
          <div className="rm-progress-fill" style={{ width: `${stats.progressPercent}%`, background: `linear-gradient(90deg, ${ph.color}, ${ph.color}aa)` }} />
        </div>
        <div style={{ display: "flex", justifyContent: "space-between", marginTop: 5 }}>
          <span style={{ fontSize: ".7rem", color: "var(--rm-muted)" }}>Day 1</span>
          <span style={{ fontSize: ".7rem", color: "var(--rm-muted)" }}>Day {config.planLength}</span>
        </div>
      </div>

      {/* ── Today Card ── */}
      {todayData && (
        <div className="rm-today-card" onClick={() => onOpenDay(today)} style={{ borderColor: `${ph.color}60`, marginBottom: 14 }}>
          <div className="rm-today-accent" style={{ background: `linear-gradient(90deg, ${ph.color}, ${ph.color}66)` }} />
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", paddingTop: 4 }}>
            <div style={{ flex: 1, paddingRight: 10 }}>
              <div className="rm-label" style={{ color: ph.color, marginBottom: 6 }}>Today — Day {today}</div>
              <div className="rm-h3" style={{ fontFamily: "var(--rm-font-display)", fontSize: "1.05rem", fontWeight: 700, lineHeight: 1.3 }}>
                {todayData.title}
              </div>
              <div style={{ fontSize: ".8rem", color: "var(--rm-muted)", lineHeight: 1.55, marginTop: 6,
                display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden" }}>
                {todayData.challenge}
              </div>
            </div>
            {progress[`day_${today}`]?.completed
              ? <div style={{ background: "var(--rm-sage)", color: "#fff", padding: "7px 14px", borderRadius: 99, fontSize: ".75rem", fontWeight: 700, whiteSpace: "nowrap", alignSelf: "flex-start" }}>✓ Done</div>
              : <div style={{ background: ph.color, color: "#fff", padding: "7px 14px", borderRadius: 99, fontSize: ".75rem", fontWeight: 700, whiteSpace: "nowrap", alignSelf: "flex-start" }}>Open →</div>
            }
          </div>
        </div>
      )}

      {/* ── Quick Actions ── */}
      <div className="rm-quick-grid" style={{ marginBottom: 14 }}>
        <button className="rm-quick-btn" onClick={onLogUrge}
          style={{ "--hover-border": "var(--rm-danger)" }}>
          <div style={{ fontSize: "1.6rem" }}>🌊</div>
          <div style={{ fontWeight: 700, color: "var(--rm-ink)", fontSize: ".84rem" }}>Log Urge</div>
          <div style={{ fontSize: ".72rem", color: "var(--rm-muted)" }}>Tap when craving hits</div>
        </button>
        <UrgeGraphMini />
      </div>

      {/* ── Journey Calendar ── */}
      <div className="rm-section" style={{ marginBottom: 14 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
          <div className="rm-h3">Your Journey</div>
          <div style={{ fontSize: ".72rem", color: "var(--rm-muted)" }}>{completedCount}/{config.planLength} complete</div>
        </div>
        <div className="rm-day-grid">
          {days.map(d => {
            const dp      = progress[`day_${d.day}`];
            const isToday = d.day === today;
            const isFuture = d.day > today;
            const isDone  = dp?.completed;
            const dph     = getPhase(d.phase);
            return (
              <div key={d.day} className="rm-day-dot"
                data-future={isFuture ? "true" : "false"}
                onClick={() => !isFuture && onOpenDay && onOpenDay(d.day)}
                title={`Day ${d.day}${isDone ? " ✓" : isToday ? " (today)" : ""}`}
                style={{
                  background:  isDone ? dph.color : isToday ? dph.wash : "var(--rm-surface)",
                  borderColor: isDone ? dph.color : isToday ? dph.color : "var(--rm-sand-light)",
                  color:       isDone ? "#fff"    : isToday ? dph.color : "var(--rm-muted)",
                  opacity:     isFuture ? .3 : 1,
                  boxShadow:   isToday ? `0 0 10px ${dph.color}55` : "none",
                }}>
                {isDone ? "✓" : d.day}
              </div>
            );
          })}
        </div>
        <div className="rm-phase-legend">
          {Object.entries(PHASE_STYLES).map(([name, s]) => (
            <div key={name} className="rm-phase-dot">
              <div style={{ width: 8, height: 8, borderRadius: 3, background: s.color, flexShrink: 0 }} />
              {name}
            </div>
          ))}
        </div>
      </div>

      {/* ── Recent Badges ── */}
      {earnedBadges.length > 0 && (
        <div className="rm-section" style={{ marginBottom: 14 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
            <div className="rm-h3">Recent Badges</div>
            <button onClick={onViewBadges} style={{
              background: "none", border: "none", color: "var(--rm-brand)",
              cursor: "pointer", fontSize: ".8rem", fontWeight: 700, fontFamily: "var(--rm-font)",
            }}>View All →</button>
          </div>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            {earnedBadges.slice(-6).map(id => {
              const badge = ALL_BADGES.find(b => b.id === id);
              return badge ? (
                <div key={id} style={{
                  background: "var(--rm-surface)",
                  border: "1px solid var(--rm-sand-light)",
                  borderRadius: 12,
                  padding: "9px 13px",
                  display: "flex", alignItems: "center", gap: 8,
                }}>
                  <span style={{ fontSize: "1.2rem" }}>{badge.emoji}</span>
                  <span style={{ fontSize: ".75rem", fontWeight: 700, color: "var(--rm-ink)", fontFamily: "var(--rm-font)" }}>{badge.label}</span>
                </div>
              ) : null;
            })}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── DAY VIEW ─────────────────────────────────────────────────────────────────
function DayView({ dayNumber, config, onBack, onComplete, onStruggled, onRelapse }) {
  const dayData  = getDayData(dayNumber);
  const dp       = getDayProgress(dayNumber);
  const today    = getTodayDay();
  const isToday  = dayNumber === today;
  const [reflection, setReflection] = useState(dp.reflection || "");
  const [showRelapse, setShowRelapse] = useState(false);

  if (!dayData) return (
    <div className="rm-content rm-animate-in" style={{ paddingTop: 40, textAlign: "center" }}>
      <div style={{ fontSize: "2rem", marginBottom: 12 }}>🌿</div>
      <div style={{ color: "var(--rm-muted)", marginBottom: 20 }}>Day {dayNumber} data not found.</div>
      <button className="rm-btn-ghost" style={{ maxWidth: 200, margin: "0 auto" }} onClick={onBack}>← Back</button>
    </div>
  );

  const ph = getPhase(dayData.phase);
  const sections = [
    { emoji: "🌅", label: "Morning Ritual",    content: dayData.morning },
    { emoji: "🎯", label: "Today's Challenge", content: dayData.challenge },
    { emoji: "🧠", label: "The Science",       content: dayData.science },
    { emoji: "💡", label: "Craving Buster",    content: dayData.cravingBuster },
    { emoji: "📊", label: "Track Today",       content: dayData.track },
  ];

  return (
    <div style={{ background: "var(--rm-bg)", minHeight: "100vh", minHeight: "100dvh", display: "flex", flexDirection: "column" }}>
      {/* Header */}
      <div style={{
        padding: "calc(14px + env(safe-area-inset-top, 0)) 16px 14px",
        background: "var(--rm-card)",
        borderBottom: "1px solid var(--rm-sand-light)",
        boxShadow: "var(--rm-sh-sm)",
        position: "sticky", top: 0, zIndex: 10,
        flexShrink: 0,
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <IconBtn onClick={onBack}>←</IconBtn>
          <div style={{ flex: 1 }}>
            <div className="rm-label" style={{ color: ph.color }}>Day {dayNumber} · {dayData.phase}</div>
            <div style={{ fontFamily: "var(--rm-font-display)", fontWeight: 700, fontSize: "1.05rem", color: "var(--rm-ink)", lineHeight: 1.2, marginTop: 2 }}>
              {dayData.title}
            </div>
          </div>
          {dp.completed && (
            <div style={{ background: "var(--rm-sage)", color: "#fff", padding: "5px 12px", borderRadius: 99, fontSize: ".72rem", fontWeight: 700, flexShrink: 0 }}>
              ✓ Done
            </div>
          )}
        </div>
      </div>

      {/* Scrollable Content */}
      <div style={{ flex: 1, overflowY: "auto", WebkitOverflowScrolling: "touch", padding: "14px 14px 0" }}>

        {/* Why reminder */}
        <div style={{ background: ph.wash, border: `1px solid ${ph.color}30`, borderLeft: `4px solid ${ph.color}`, borderRadius: "0 12px 12px 0", padding: "12px 14px", marginBottom: 12 }}>
          <div style={{ fontSize: ".72rem", fontWeight: 700, color: ph.color, marginBottom: 4 }}>Remember why you're here</div>
          <div style={{ fontSize: ".84rem", color: "var(--rm-ink)", fontStyle: "italic", lineHeight: 1.6 }}>"{config?.why}"</div>
        </div>

        {/* Sections */}
        <div style={{ display: "flex", flexDirection: "column", gap: 10, marginBottom: 10 }}>
          {sections.map((sec, i) => (
            <div key={i} className="rm-day-section">
              <div className="rm-day-section-header">
                <div className="rm-day-section-icon">{sec.emoji}</div>
                <div className="rm-label">{sec.label}</div>
              </div>
              <div style={{ fontSize: ".88rem", color: "var(--rm-ink)", lineHeight: 1.75 }}>{sec.content}</div>
            </div>
          ))}
        </div>

        {/* Evening Reflection */}
        <div className="rm-day-section" style={{ marginBottom: 10, borderColor: `${ph.color}40` }}>
          <div className="rm-day-section-header">
            <div className="rm-day-section-icon" style={{ background: ph.wash }}>🌙</div>
            <div className="rm-label" style={{ color: ph.color }}>Evening Reflection</div>
          </div>
          <div style={{ fontSize: ".86rem", color: "var(--rm-ink)", marginBottom: 12, lineHeight: 1.7 }}>{dayData.reflection}</div>
          <textarea className="rm-textarea"
            value={reflection}
            onChange={e => setReflection(e.target.value)}
            placeholder="Write your answer here... (optional but powerful)"
            rows={3}
            disabled={dp.completed}
            style={{ opacity: dp.completed ? .6 : 1 }}
          />
        </div>

        {/* Completed banner */}
        {dp.completed && (
          <div className="rm-completed-banner" style={{ marginBottom: 10 }}>
            <div style={{ fontSize: "2rem", marginBottom: 6 }}>🎉</div>
            <div style={{ fontWeight: 700, color: "var(--rm-sage)", fontFamily: "var(--rm-font-display)" }}>Day {dayNumber} Complete</div>
            <div style={{ fontSize: ".75rem", color: "var(--rm-muted)", marginTop: 4 }}>
              Completed {new Date(dp.completedAt).toLocaleDateString()}
            </div>
          </div>
        )}

        <div style={{ height: 20 }} />
      </div>

      {/* Sticky Action Bar */}
      {!dp.completed && isToday && (
        <div className="rm-action-bar">
          <button className="rm-btn-primary" style={{ background: ph.color, boxShadow: `0 4px 16px ${ph.color}40` }}
            onClick={() => onComplete(reflection)}>
            ✅ Complete Day {dayNumber}
          </button>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
            <button className="rm-btn-ghost" style={{ borderColor: "var(--rm-peach)", color: "var(--rm-peach)" }} onClick={onStruggled}>
              💙 Struggled
            </button>
            <button className="rm-btn-ghost" style={{ borderColor: "var(--rm-danger)", color: "var(--rm-danger)" }} onClick={() => setShowRelapse(true)}>
              ⚠️ Relapsed
            </button>
          </div>
        </div>
      )}

      {showRelapse && (
        <ConfirmModal
          title="Report a Relapse?"
          message="This is an act of honesty, not failure. You'll go back 2 days — not to Day 1. Recovery isn't linear. You're still doing this."
          confirmLabel="Yes, Handle with Compassion"
          warning
          onConfirm={() => { setShowRelapse(false); onRelapse(); }}
          onCancel={() => setShowRelapse(false)}
        />
      )}
    </div>
  );
}

// ─── BADGES VIEW ──────────────────────────────────────────────────────────────
function BadgesView({ badges, onBack }) {
  return (
    <div className="rm-content rm-animate-in">
      <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 24 }}>
        <IconBtn onClick={onBack}>←</IconBtn>
        <div style={{ flex: 1 }}>
          <div className="rm-label" style={{ color: "var(--rm-brand)" }}>Your Achievements</div>
          <div className="rm-h2">Badges</div>
        </div>
        <div style={{ fontSize: ".8rem", color: "var(--rm-muted)", fontFamily: "var(--rm-font)" }}>
          {badges.length}/{ALL_BADGES.length} earned
        </div>
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
        {ALL_BADGES.map(badge => {
          const earned = badges.includes(badge.id);
          return (
            <div key={badge.id} className={`rm-badge-item ${earned ? "earned" : ""}`}>
              <div style={{ fontSize: "1.8rem", filter: earned ? "none" : "grayscale(1)", flexShrink: 0 }}>{badge.emoji}</div>
              <div>
                <div style={{ fontWeight: 700, color: "var(--rm-ink)", fontSize: ".84rem", fontFamily: "var(--rm-font)" }}>{badge.label}</div>
                <div style={{ fontSize: ".72rem", color: "var(--rm-muted)", marginTop: 2 }}>{badge.description}</div>
                {!earned && <div style={{ fontSize: ".68rem", color: "var(--rm-sand)", marginTop: 4, fontFamily: "var(--rm-font)" }}>🔒 Locked</div>}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ─── SETTINGS VIEW ────────────────────────────────────────────────────────────
function SettingsView({ config, onBack, onDelete }) {
  const addictionMeta = config ? getAddictionMeta(config.addiction) : null;
  return (
    <div className="rm-content rm-animate-in">
      <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 24 }}>
        <IconBtn onClick={onBack}>←</IconBtn>
        <div className="rm-h2">Settings</div>
      </div>

      {config && (
        <div className="rm-section" style={{ marginBottom: 14 }}>
          <div className="rm-label" style={{ marginBottom: 12 }}>Your Roadmap</div>
          {[
            ["Name", config.name],
            ["Addiction", `${addictionMeta?.emoji || ""} ${addictionMeta?.label || ""}`],
            ["Plan Length", `${config.planLength} days`],
            ["Severity", config.severity],
            ["Started", new Date(config.startDate).toLocaleDateString()],
          ].map(([label, value]) => (
            <div key={label} className="rm-settings-row">
              <span style={{ color: "var(--rm-muted)", fontSize: ".84rem", fontFamily: "var(--rm-font)" }}>{label}</span>
              <span style={{ color: "var(--rm-ink)", fontSize: ".84rem", fontWeight: 600, textTransform: "capitalize", fontFamily: "var(--rm-font)" }}>{value}</span>
            </div>
          ))}
        </div>
      )}

      <div className="rm-danger-zone">
        <div className="rm-label" style={{ color: "var(--rm-danger)", marginBottom: 8 }}>Danger Zone</div>
        <div style={{ fontSize: ".82rem", color: "var(--rm-muted)", marginBottom: 16, lineHeight: 1.65 }}>
          Deleting your roadmap will permanently remove all your progress, streak, badges, and reflections. This cannot be undone.
        </div>
        <button className="rm-btn-danger" onClick={onDelete}>🗑️ Delete My Roadmap</button>
      </div>
    </div>
  );
}

// ─── MINI URGE GRAPH ──────────────────────────────────────────────────────────
function UrgeGraphMini() {
  const urgesByDay = getUrgesByDay();
  const today      = getTodayDay();
  const days       = Array.from({ length: Math.min(today, 14) }, (_, i) => today - 13 + i).filter(d => d > 0);
  const max        = Math.max(...days.map(d => urgesByDay[d] || 0), 1);

  return (
    <div className="rm-quick-btn" style={{ cursor: "default" }}>
      <div className="rm-label" style={{ alignSelf: "flex-start", marginBottom: 4 }}>Urge Trend ↓</div>
      <div style={{ display: "flex", alignItems: "flex-end", gap: 2, height: 44, width: "100%" }}>
        {days.map(d => {
          const count  = urgesByDay[d] || 0;
          const h      = max > 0 ? Math.round((count / max) * 38) + 4 : 4;
          const isToday = d === today;
          return (
            <div key={d} style={{
              flex: 1, height: h, borderRadius: 3,
              background: isToday ? "var(--rm-brand)" : "var(--rm-sage)",
              opacity: isToday ? 1 : .5,
              transition: "height .3s",
            }} title={`Day ${d}: ${count} urges`} />
          );
        })}
      </div>
    </div>
  );
}

// ─── UPGRADE MODAL ────────────────────────────────────────────────────────────
function UpgradeModal({ onUpgrade, onDecline }) {
  return (
    <div className="rm-overlay rm-overlay-desktop">
      <div className="rm-modal rm-modal-desktop" style={{ maxWidth: 460, textAlign: "center" }}>
        <div className="rm-modal-handle" style={{ margin: "0 auto 20px" }} />
        <div style={{ fontSize: "2.5rem", marginBottom: 14 }}>🏆</div>
        <div className="rm-h2" style={{ marginBottom: 10 }}>You Completed 21 Days!</div>
        <p className="rm-muted" style={{ marginBottom: 20 }}>
          Your 21-day foundation is complete. Your brain has begun rewiring. Continue to the 60-Day Foundation?
        </p>
        <div className="rm-upgrade-highlight" style={{ background: "var(--rm-brand-wash)", border: "1px solid rgba(90,105,147,.2)", marginBottom: 20 }}>
          <div style={{ fontWeight: 700, color: "var(--rm-brand)", marginBottom: 8, fontSize: ".88rem" }}>60-Day Foundation includes:</div>
          <div className="rm-muted" style={{ lineHeight: 1.8 }}>
            39 more days of Deep Integration, Mastery & Legacy phases<br />
            All your progress carries over from Day 22<br />
            Advanced identity and relationship work
          </div>
        </div>
        <div style={{ display: "flex", gap: 10 }}>
          <button className="rm-btn-ghost" style={{ flex: 1 }} onClick={onDecline}>Stay at 21 Days</button>
          <button className="rm-btn-primary" style={{ flex: 1 }} onClick={onUpgrade}>🚀 Upgrade to 60 Days</button>
        </div>
      </div>
    </div>
  );
}

// ─── CONFIRM MODAL ────────────────────────────────────────────────────────────
function ConfirmModal({ title, message, confirmLabel, danger, warning, onConfirm, onCancel }) {
  const btnColor = danger ? "#dc2626" : warning ? "#C4845A" : "var(--rm-brand)";
  return (
    <div className="rm-overlay">
      <div className="rm-modal">
        <div className="rm-modal-handle" />
        <div className="rm-h2" style={{ marginBottom: 10 }}>{title}</div>
        <p className="rm-muted" style={{ marginBottom: 24 }}>{message}</p>
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          <button className="rm-btn-primary" style={{ background: btnColor, boxShadow: "none" }} onClick={onConfirm}>
            {confirmLabel}
          </button>
          <button className="rm-btn-ghost" onClick={onCancel}>Cancel</button>
        </div>
      </div>
    </div>
  );
}

// ─── CONFETTI ─────────────────────────────────────────────────────────────────
function ConfettiOverlay({ onDone }) {
  useEffect(() => { const t = setTimeout(onDone, 3000); return () => clearTimeout(t); }, []);
  const pieces = Array.from({ length: 40 }, (_, i) => ({
    left: `${Math.random() * 100}%`,
    delay: `${Math.random() * 0.8}s`,
    color: ["#5A6993","#7B8F8A","#E6A184","#C4845A","#8a7b9b","#ffffff"][i % 6],
    size: 6 + Math.random() * 7,
  }));
  return (
    <div style={{ position: "fixed", inset: 0, pointerEvents: "none", zIndex: 8000, overflow: "hidden" }}>
      {pieces.map((p, i) => (
        <div key={i} style={{
          position: "absolute", top: 0, left: p.left,
          width: p.size, height: p.size,
          background: p.color, borderRadius: "50%",
          animation: `confettiFall 2.5s ${p.delay} ease-in forwards`,
        }} />
      ))}
    </div>
  );
}